import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, api } from '../lib/api';

/**
 * The demo-request form — the conversion path on the public page.
 *
 * POST /api/demo-requests has existed since phase 7 with nothing in this repo pointing at it;
 * this is the first caller. Three server behaviours shape the UI:
 *
 *  - The endpoint always answers 202, stored or throttled alike, so success copy must promise
 *    contact, never confirm storage.
 *  - The `website` field is a honeypot. It must be present, empty for humans, and invisible —
 *    hidden from layout, from the tab order, and from assistive tech.
 *  - Requests land in the demo_requests table BEFORE any notification email is attempted, so
 *    this path works even while outbound mail is unproven. That is why the page leads with a
 *    form rather than a mailto: to an address that cannot yet receive.
 */
export function RequestWorkspace() {
  const [form, setForm] = useState({
    carrierName: '',
    contactName: '',
    email: '',
    phone: '',
    dotNumber: '',
    driverCount: '',
    message: '',
    website: '',
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = useMutation({
    mutationFn: () =>
      api.post('/api/demo-requests', {
        carrierName: form.carrierName,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone || null,
        dotNumber: form.dotNumber || null,
        driverCount: form.driverCount ? Number(form.driverCount) : null,
        message: form.message || null,
        source: 'app-login',
        website: form.website,
      }),
  });

  if (submit.isSuccess) {
    return (
      <div className="rw-done" role="status">
        <p className="rw-done-mark" aria-hidden="true">✓</p>
        <h3 className="rw-done-title">We have it{form.contactName ? `, ${form.contactName.split(' ')[0]}` : ''}.</h3>
        <p className="rw-done-body">
          Someone from Milepost Safety Group will reach out to {form.email} to set up a short
          walkthrough and get your workspace provisioned.
        </p>
      </div>
    );
  }

  const fieldErrors = submit.error instanceof ApiError ? submit.error.byField() : {};

  /*
   * Fields whose errors render beside their own input. An error for any OTHER field must fall
   * through to the general alert — the original version suppressed the generic message whenever
   * fieldErrors was non-empty, so a 400 naming only `phone` or `message` rendered nothing at
   * all: the visitor's form sat there, intact and silent. Found in independent review.
   */
  const shownBeside = new Set(['carrierName', 'contactName', 'email', 'dotNumber']);
  const FIELD_LABEL: Record<string, string> = {
    phone: 'Phone',
    driverCount: 'Drivers on your roster',
    message: 'Message',
  };
  const orphaned = Object.entries(fieldErrors).filter(([field]) => !shownBeside.has(field));

  const generalError =
    submit.error instanceof ApiError && submit.error.status === 429
      ? 'Too many requests from this network just now — please try again in a little while.'
      : orphaned.length > 0
        ? orphaned.map(([f, m]) => `${FIELD_LABEL[f] ?? f}: ${m}`).join('. ')
        : submit.error && Object.keys(fieldErrors).length === 0
          ? 'That did not go through. Please try again.'
          : null;

  /** aria-invalid + aria-describedby for one input, so failures reach assistive tech. */
  const invalid = (field: string) =>
    fieldErrors[field]
      ? { 'aria-invalid': true as const, 'aria-describedby': `rw-${field}-error` }
      : {};

  return (
    <form
      className="rw-form"
      onSubmit={(e) => {
        e.preventDefault();
        submit.mutate();
      }}
    >
      <div className="rw-grid">
        <div className="field">
          <label htmlFor="rw-carrier">Carrier name</label>
          <input id="rw-carrier" type="text" required minLength={2} maxLength={200}
                 autoComplete="organization" value={form.carrierName} onChange={set('carrierName')} {...invalid('carrierName')} />
          {fieldErrors.carrierName && <p className="field-error" id="rw-carrierName-error">{fieldErrors.carrierName}</p>}
        </div>

        <div className="field">
          <label htmlFor="rw-name">Your name</label>
          <input id="rw-name" type="text" required minLength={2} maxLength={200}
                 autoComplete="name" value={form.contactName} onChange={set('contactName')} {...invalid('contactName')} />
          {fieldErrors.contactName && <p className="field-error" id="rw-contactName-error">{fieldErrors.contactName}</p>}
        </div>

        <div className="field">
          <label htmlFor="rw-email">Work email</label>
          <input id="rw-email" type="email" required maxLength={254}
                 autoComplete="email" value={form.email} onChange={set('email')} {...invalid('email')} />
          {fieldErrors.email && <p className="field-error" id="rw-email-error">{fieldErrors.email}</p>}
        </div>

        <div className="field">
          <label htmlFor="rw-phone">Phone <span className="rw-optional">optional</span></label>
          <input id="rw-phone" type="tel" maxLength={32}
                 autoComplete="tel" value={form.phone} onChange={set('phone')} />
        </div>

        <div className="field">
          <label htmlFor="rw-dot">USDOT number <span className="rw-optional">optional</span></label>
          <input id="rw-dot" type="text" inputMode="numeric" pattern="[0-9]{1,8}"
                 title="1 to 8 digits" value={form.dotNumber} onChange={set('dotNumber')} {...invalid('dotNumber')} />
          {fieldErrors.dotNumber && <p className="field-error" id="rw-dotNumber-error">{fieldErrors.dotNumber}</p>}
        </div>

        <div className="field">
          <label htmlFor="rw-count">Drivers on your roster <span className="rw-optional">optional</span></label>
          <input id="rw-count" type="number" min={0} max={100000}
                 value={form.driverCount} onChange={set('driverCount')} />
        </div>
      </div>

      <div className="field">
        <label htmlFor="rw-message">
          Anything we should know? <span className="rw-optional">optional</span>
        </label>
        <textarea id="rw-message" rows={3} maxLength={4000}
                  placeholder="e.g. Mixed CDL and non-CDL fleet, next insurance audit in the spring."
                  value={form.message} onChange={set('message')} />
      </div>

      {/*
        The honeypot. Real visitors never see or reach it; naive bots fill every input they find.
        display:none would be simplest but some bots skip hidden fields — off-screen is the
        conventional middle ground. Never add a label that could end up read aloud.
      */}
      <div className="rw-hp" aria-hidden="true">
        <input type="text" name="website" tabIndex={-1} autoComplete="off"
               value={form.website} onChange={set('website')} />
      </div>

      {/* role="alert" so an async failure is announced, not just painted. */}
      {generalError && (
        <p className="field-error rw-general-error" role="alert">
          {generalError}
        </p>
      )}

      <div className="rw-actions">
        <button type="submit" className="btn rw-submit" disabled={submit.isPending}>
          {submit.isPending ? 'Sending…' : 'Request a workspace'}
        </button>
        <p className="rw-fineprint">
          No self-serve signup, no card required. A person sets up every workspace.
        </p>
      </div>
    </form>
  );
}
